import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Paperclip } from "lucide-react";
import StakeholderPicker from "@/components/common/StakeholderPicker";
import EmailPreviewFrame from "@/components/settings/EmailPreviewFrame";
import TrialLocked from "@/components/trial/TrialLocked";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  getOrganizationSettingsErrorMessage,
  organizationSettingsService,
  type RecipientPicker,
} from "@/services/organizationSettings";

// Outside the settings key: a chip toggle does not change the previews.
const PREVIEW_QUERY_KEY = ["automatedEmailPreview", "documents"] as const;

interface DocumentsEmailPanelProps {
  picker: RecipientPicker | undefined;
  disabled: boolean;
  onToggle: (party: string, next: boolean) => void;
}

/**
 * The signed-documents email: always sent, so only who receives it is
 * configurable. Each audience's copy is previewed read-only.
 */
const DocumentsEmailPanel: React.FC<DocumentsEmailPanelProps> = ({
  picker,
  disabled,
  onToggle,
}) => {
  const previews = useQuery({
    queryKey: PREVIEW_QUERY_KEY,
    queryFn: organizationSettingsService.previewDocumentsEmails,
  });
  const [audience, setAudience] = useState<string | null>(null);
  const shown =
    previews.data?.find((preview) => preview.audience === audience) ?? previews.data?.[0];

  return (
    <div className="space-y-4">
      {picker && (
        <div className="space-y-2">
          <Label>Who receives the signed documents</Label>
          <TrialLocked lock="documentsRecipients">
            <StakeholderPicker
              parties={picker.parties}
              value={picker.value}
              onToggle={onToggle}
              disabled={disabled}
              aria-label="Who receives the signed documents"
            />
          </TrialLocked>
          <p className="text-xs text-muted-foreground">
            The signed contract copy recipients above always get a copy. Merchant Tasks use these
            chips.
            {picker.hint && <> {picker.hint}</>}
          </p>
        </div>
      )}

      <div className="space-y-2" data-testid="documents-email-preview">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label>Preview</Label>
          {previews.data && previews.data.length > 1 && (
            <ToggleGroup
              type="single"
              size="sm"
              value={shown?.audience}
              onValueChange={(value) => value && setAudience(value)}
              aria-label="Preview as"
              // Phone: wrap onto rows with labels that grow in height; desktop keeps one row.
              className="flex-wrap justify-start sm:justify-center"
            >
              {previews.data.map((preview) => (
                <ToggleGroupItem
                  key={preview.audience}
                  value={preview.audience}
                  className="h-auto min-h-8 px-2.5 py-1 sm:h-8 sm:px-1.5 sm:py-0"
                >
                  {preview.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          )}
        </div>
        <EmailPreviewFrame
          subject={shown?.subject ?? ""}
          html={shown?.html ?? ""}
          loading={previews.isFetching}
          error={previews.error ? getOrganizationSettingsErrorMessage(previews.error) : null}
          className="h-[28rem]"
        />
        {shown && shown.attachments.length > 0 && (
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <Paperclip className="h-3 w-3" aria-hidden />
            Attached: {shown.attachments.join(", ")}
          </p>
        )}
      </div>
    </div>
  );
};

export default DocumentsEmailPanel;
